// The operator's editor for his waiting-room questions, opened from the pencil
// on the trivia card in his own waiting room preview (QUEUE row 431; Adam,
// 2026-10-18: "the trivia card for me would have a pencil icon which would
// expand an inline editor bar where i can reorder/edit/delete/add"). Tap a
// question (or its pencil) to rewrite it and its answers, drag the grip to
// move it, Delete in the form takes it out, and Add a question writes a new one.
// A drag (QUEUE row 449) lifts the row under the pointer, slides the others
// aside, scrolls the card at its top and bottom edges, and changes the order
// once, on release: reordering the rows mid-drag moved the grip out from under
// the pointer and the drag stalled. The order saved
// here is the order a guest's first round deals them (WaitingTrivia's
// firstDeal). The questions stay in Seamless's facts bank;
// worker/src/triviabank.ts passes these calls through to it, so nothing here
// keeps a copy. The search icon at the top filters the list by words in the
// question or its answers, in any order (Seamless QUEUE row 1849: "a minimal
// search icon where i can search keywords in the questions as we get more of
// them"); while it is open the grips rest and Add a question steps aside, and
// the X puts the whole list back.

import { useDeferredValue, useEffect, useRef, useState, type CSSProperties, type JSX, type KeyboardEvent, type PointerEvent } from "react";
import { api, ApiError } from "../lib/api";
import { CheckIcon, CloseIcon, GripIcon, PencilIcon, PlusIcon, SearchIcon } from "./icons";

export interface BankQuestion {
  readonly id: string;
  readonly question: string;
  readonly choices: readonly string[];
  readonly answer: number;
  readonly placed?: boolean;
}

const MAX_CHOICES = 4;
const NEW = "new";
/** How close to the card's top or bottom edge a dragged row starts it scrolling. */
const EDGE_PX = 56;
/** The fastest the card scrolls under a dragged row, in pixels a frame. */
const SCROLL_STEP = 14;

interface Drag {
  readonly id: string;
  readonly pointerId: number;
  readonly from: number;
  /** Each row's top and height in the list when the drag began; transforms do not move these. */
  readonly tops: readonly number[];
  readonly heights: readonly number[];
  readonly startY: number;
  readonly startScroll: number;
  readonly scroller: HTMLElement | null;
  y: number;
}

/** The scrolling box the list sits in (the trivia card), if any. */
function scrollParent(el: HTMLElement): HTMLElement | null {
  for (let p = el.parentElement; p; p = p.parentElement) {
    const o = getComputedStyle(p).overflowY;
    if ((o === "auto" || o === "scroll") && p.scrollHeight > p.clientHeight) return p;
  }
  return null;
}

/** How far the lifted row has traveled: the pointer's travel plus however far the card has scrolled since. */
function travel(d: Drag): number {
  return d.y - d.startY + (d.scroller ? d.scroller.scrollTop - d.startScroll : 0);
}

/** Where the lifted row lands: the number of other rows whose middle is above its middle. */
function landing(d: Drag, dy: number): number {
  const mid = d.tops[d.from]! + d.heights[d.from]! / 2 + dy;
  let to = 0;
  d.tops.forEach((top, i) => {
    if (i !== d.from && top + d.heights[i]! / 2 < mid) to++;
  });
  return to;
}
/** Every word typed appears in the question or one of its answers, in any order, ignoring case (Seamless's todosearch rule). */
export function matchesQuestion(q: Pick<BankQuestion, "question" | "choices">, query: string): boolean {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return true;
  const hay = `${q.question} ${q.choices.join(" ")}`.toLowerCase();
  return words.every((w) => hay.includes(w));
}

const message = (err: unknown, fallback: string): string => (err instanceof ApiError && err.status !== 0 ? err.message : fallback);

/** `onBank` hears every bank the server sends back, so the card can deal the new set when the editor closes. */
export function TriviaEditor({ onBank }: { readonly onBank: (bank: BankQuestion[]) => void }): JSX.Element {
  const [rows, setRowsState] = useState<BankQuestion[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [searching, setSearching] = useState(false);
  const [query, setQuery] = useState("");
  // The field repaints on each key and the list after it, so typing never waits on the rows.
  const shownQuery = useDeferredValue(searching ? query.trim() : "");
  // The lifted row's offset from where it started, and the slot it would land in.
  const [lift, setLift] = useState<{ id: string; dy: number; to: number } | null>(null);
  const dragging = lift?.id ?? null;
  const drag = useRef<Drag | null>(null);
  const frame = useRef<number | null>(null);
  const listRef = useRef<HTMLOListElement>(null);
  const before = useRef<BankQuestion[] | null>(null);
  const keyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tell = useRef(onBank);
  tell.current = onBank;

  const setRows = (next: BankQuestion[]): void => {
    setRowsState(next);
    tell.current(next);
  };

  useEffect(() => {
    const controller = new AbortController();
    api<{ questions: BankQuestion[] }>("/api/trivia/bank", { signal: controller.signal })
      .then((r) => setRows(r.questions))
      .catch((err: unknown) => {
        if ((err as Error).name !== "AbortError") setError(message(err, "Couldn't load the questions."));
      });
    return () => {
      controller.abort();
      if (keyTimer.current) clearTimeout(keyTimer.current);
      if (frame.current !== null) cancelAnimationFrame(frame.current);
    };
    // Loaded once when the editor opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveOrder = (next: BankQuestion[], was: BankQuestion[]): void => {
    if (next.map((q) => q.id).join() === was.map((q) => q.id).join()) return;
    setError(null);
    api<{ questions: BankQuestion[] }>("/api/trivia/order", { method: "POST", body: { ids: next.map((q) => q.id) } })
      .then((r) => setRows(r.questions))
      .catch((err: unknown) => {
        setRows(was);
        setError(message(err, "Couldn't save the order."));
      });
  };

  const follow = (): void => {
    const d = drag.current;
    if (!d) return;
    const dy = travel(d);
    const to = landing(d, dy);
    setLift((l) => {
      if (l && l.to !== to) navigator.vibrate?.(4);
      return { id: d.id, dy, to };
    });
  };
  // While the pointer is near the card's top or bottom edge, scroll it, faster nearer the edge.
  const edgeScroll = (): void => {
    frame.current = null;
    const d = drag.current;
    if (!d?.scroller) return;
    const r = d.scroller.getBoundingClientRect();
    const near = d.y < r.top + EDGE_PX ? d.y - (r.top + EDGE_PX) : d.y > r.bottom - EDGE_PX ? d.y - (r.bottom - EDGE_PX) : 0;
    if (!near) return;
    const before = d.scroller.scrollTop;
    d.scroller.scrollTop = before + Math.sign(near) * Math.min(SCROLL_STEP, Math.ceil((Math.abs(near) / EDGE_PX) * SCROLL_STEP));
    if (d.scroller.scrollTop !== before) follow();
    frame.current = requestAnimationFrame(edgeScroll);
  };
  const onGripDown = (id: string) => (e: PointerEvent<HTMLButtonElement>): void => {
    if (!rows || editing || drag.current || e.button !== 0 || !listRef.current) return;
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    const items = Array.from(listRef.current.querySelectorAll<HTMLElement>(":scope > .tq"));
    const scroller = scrollParent(listRef.current);
    const from = rows.findIndex((q) => q.id === id);
    drag.current = {
      id,
      pointerId: e.pointerId,
      from,
      tops: items.map((el) => el.offsetTop),
      heights: items.map((el) => el.offsetHeight),
      startY: e.clientY,
      startScroll: scroller?.scrollTop ?? 0,
      scroller,
      y: e.clientY,
    };
    setLift({ id, dy: 0, to: from });
    navigator.vibrate?.(8);
  };
  const onGripMove = (e: PointerEvent<HTMLButtonElement>): void => {
    const d = drag.current;
    if (!d || e.pointerId !== d.pointerId) return;
    d.y = e.clientY;
    follow();
    if (frame.current === null) frame.current = requestAnimationFrame(edgeScroll);
  };
  // Release puts the row in its slot and saves; a cancelled pointer puts everything back.
  const endDrag = (keep: boolean) => (e: PointerEvent<HTMLButtonElement>): void => {
    const d = drag.current;
    if (!d || e.pointerId !== d.pointerId) return;
    drag.current = null;
    if (frame.current !== null) cancelAnimationFrame(frame.current);
    frame.current = null;
    const to = landing(d, travel(d));
    setLift(null);
    if (!keep || !rows || to === d.from) return;
    const next = rows.slice();
    const [moved] = next.splice(d.from, 1);
    next.splice(to, 0, moved!);
    setRowsState(next);
    saveOrder(next, rows);
  };
  // Where each row sits during a drag: the lifted one under the pointer, the
  // ones it has passed moved over by its height to open its slot.
  const shift = (i: number): CSSProperties | undefined => {
    const d = drag.current;
    if (!lift || !d) return undefined;
    if (i === d.from) return { transform: `translateY(${lift.dy}px)`, transition: "box-shadow 0.15s ease" };
    const h = d.heights[d.from]!;
    if (d.from < lift.to && i > d.from && i <= lift.to) return { transform: `translateY(${-h}px)` };
    if (lift.to < d.from && i >= lift.to && i < d.from) return { transform: `translateY(${h}px)` };
    return { transform: "translateY(0)" };
  };
  // Arrow keys on a focused grip move it one place; the order saves once the keys stop.
  const onGripKey = (id: string) => (e: KeyboardEvent<HTMLButtonElement>): void => {
    if (!rows || (e.key !== "ArrowUp" && e.key !== "ArrowDown")) return;
    e.preventDefault();
    const from = rows.findIndex((q) => q.id === id);
    const to = from + (e.key === "ArrowUp" ? -1 : 1);
    if (to < 0 || to >= rows.length) return;
    if (!before.current) before.current = rows;
    const next = rows.slice();
    [next[from], next[to]] = [next[to]!, next[from]!];
    setRowsState(next);
    if (keyTimer.current) clearTimeout(keyTimer.current);
    keyTimer.current = setTimeout(() => {
      if (before.current) saveOrder(next, before.current);
      before.current = null;
    }, 700);
  };

  const saved = (bank: BankQuestion[]): void => {
    setRows(bank);
    setEditing(null);
  };

  if (!rows) {
    return error ? (
      <p className="tq-error" role="alert">
        {error}
      </p>
    ) : (
      <div className="tq-loading" aria-label="Loading the questions" />
    );
  }

  const shown = shownQuery ? rows.filter((q) => matchesQuestion(q, shownQuery)) : rows;
  const closeSearch = (): void => {
    setSearching(false);
    setQuery("");
  };

  return (
    <div className="tq-editor" data-testid="trivia-editor">
      <div className={`tq-bar${searching ? " is-searching" : ""}`}>
        {searching ? (
          <input
            className="tq-input tq-bar__field"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") closeSearch();
            }}
            placeholder="Search questions"
            aria-label="Search questions"
            autoFocus
            data-testid="trivia-search-field"
          />
        ) : null}
        <button
          type="button"
          className="tq-icon"
          aria-label={searching ? "Close search" : "Search questions"}
          title={searching ? "Close search" : "Search questions"}
          // Pressed on pointer down so the field's blur cannot eat the first tap.
          onPointerDown={(e) => e.preventDefault()}
          onClick={() => (searching ? closeSearch() : setSearching(true))}
          disabled={!!dragging}
          data-testid="trivia-search"
        >
          {searching ? <CloseIcon size={18} /> : <SearchIcon size={18} />}
        </button>
      </div>
      {error ? (
        <p className="tq-error" role="alert">
          {error}
        </p>
      ) : null}
      {shown.length ? (
        <ol className="tq-list" ref={listRef} data-testid="trivia-list">
          {shown.map((q, i) => (
            <li key={q.id} className={`tq${dragging === q.id ? " tq--lifted" : ""}${lift ? " tq--moving" : ""}`} data-id={q.id} style={shift(i)}>
              {editing === q.id ? (
                <QuestionForm q={q} onCancel={() => setEditing(null)} onSaved={saved} />
              ) : (
                <>
                  <button
                    type="button"
                    className="tq__grip"
                    aria-label={`Move question ${i + 1}`}
                    disabled={!!editing || !!shownQuery}
                    onPointerDown={onGripDown(q.id)}
                    onPointerMove={onGripMove}
                    onPointerUp={endDrag(true)}
                    onPointerCancel={endDrag(false)}
                    onKeyDown={onGripKey(q.id)}
                  >
                    <GripIcon size={18} />
                  </button>
                  <button type="button" className="tq__body" onClick={() => setEditing(q.id)} disabled={!!dragging || !!editing}>
                    <span className="tq__text">
                      <span className="tq__q">{q.question}</span>
                      <span className="tq__a">{q.choices[q.answer]}</span>
                    </span>
                    <span className="tq__pencil" aria-hidden="true">
                      <PencilIcon size={16} />
                    </span>
                  </button>
                </>
              )}
            </li>
          ))}
        </ol>
      ) : (
        <p className="tq__empty">{rows.length ? "No matches." : "No questions yet."}</p>
      )}
      {searching ? null : editing === NEW ? (
        <QuestionForm q={{ id: NEW, question: "", choices: ["", ""], answer: 0 }} onCancel={() => setEditing(null)} onSaved={saved} />
      ) : (
        <button type="button" className="tq-add" onClick={() => setEditing(NEW)} disabled={!!editing || !!dragging} data-testid="trivia-add">
          <PlusIcon size={16} />
          Add a question
        </button>
      )}
    </div>
  );
}

function QuestionForm({ q, onCancel, onSaved }: { readonly q: BankQuestion; readonly onCancel: () => void; readonly onSaved: (bank: BankQuestion[]) => void }): JSX.Element {
  const isNew = q.id === NEW;
  const [question, setQuestion] = useState(q.question);
  const [choices, setChoices] = useState<string[]>([...q.choices]);
  const [answer, setAnswer] = useState(q.answer);
  const [busy, setBusy] = useState<"save" | "delete" | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setChoice = (i: number, text: string): void => setChoices((c) => c.map((x, j) => (j === i ? text : x)));
  const removeChoice = (i: number): void => {
    setChoices((c) => c.filter((_, j) => j !== i));
    setAnswer((a) => (a === i ? 0 : a > i ? a - 1 : a));
  };
  const send = (kind: "save" | "delete"): void => {
    setBusy(kind);
    setError(null);
    const call =
      kind === "delete"
        ? api<{ questions: BankQuestion[] }>("/api/trivia/delete", { method: "POST", body: { id: q.id } })
        : api<{ questions: BankQuestion[] }>("/api/trivia/bank", { method: "POST", body: { ...(isNew ? {} : { id: q.id }), question, choices, answer } });
    call
      .then((r) => onSaved(r.questions))
      .catch((err: unknown) => {
        setError(message(err, kind === "delete" ? "Couldn't delete it." : "Couldn't save it."));
        setBusy(null);
        setConfirming(false);
      });
  };
  const fid = `tq-${q.id}`;

  return (
    <form
      className="tq-form"
      data-testid="trivia-form"
      onSubmit={(e) => {
        e.preventDefault();
        send("save");
      }}
    >
      <label className="tq-form__label" htmlFor={fid}>
        Question
      </label>
      <textarea id={fid} className="tq-input tq-form__question" value={question} onChange={(e) => setQuestion(e.target.value)} rows={2} maxLength={300} autoFocus={isNew} />
      <span className="tq-form__label" id={`${fid}-a`}>
        Answers
      </span>
      <div className="tq-form__choices" role="radiogroup" aria-labelledby={`${fid}-a`}>
        {choices.map((c, i) => (
          <div key={i} className="tq-choice">
            <button
              type="button"
              role="radio"
              aria-checked={answer === i}
              aria-label={`Answer ${i + 1} is the right one`}
              className={`tq-choice__mark${answer === i ? " is-right" : ""}`}
              onClick={() => setAnswer(i)}
            >
              {answer === i ? <CheckIcon size={16} /> : null}
            </button>
            <input className="tq-input tq-choice__text" value={c} onChange={(e) => setChoice(i, e.target.value)} maxLength={120} aria-label={`Answer ${i + 1}`} />
            {choices.length > 2 ? (
              <button type="button" className="tq-icon" onClick={() => removeChoice(i)} aria-label={`Remove answer ${i + 1}`}>
                <CloseIcon size={16} />
              </button>
            ) : null}
          </div>
        ))}
        {choices.length < MAX_CHOICES ? (
          <button type="button" className="tq-add tq-add--small" onClick={() => setChoices((c) => [...c, ""])}>
            <PlusIcon size={14} />
            Add an answer
          </button>
        ) : null}
      </div>
      {error ? (
        <p className="tq-error" role="alert">
          {error}
        </p>
      ) : null}
      <div className="tq-form__actions">
        {isNew ? null : (
          <button
            type="button"
            className={`tq-btn tq-btn--danger${confirming ? " is-confirming" : ""}`}
            onClick={() => (confirming ? send("delete") : setConfirming(true))}
            disabled={!!busy}
            aria-busy={busy === "delete"}
            data-testid="trivia-delete"
          >
            {busy === "delete" ? <span className="tq-spinner" aria-hidden="true" /> : null}
            {confirming ? "Delete it" : "Delete"}
          </button>
        )}
        <span className="tq-form__gap" />
        <button type="button" className="tq-btn" onClick={onCancel} disabled={!!busy}>
          Cancel
        </button>
        <button type="submit" className="tq-btn tq-btn--primary" disabled={!!busy} aria-busy={busy === "save"}>
          {busy === "save" ? <span className="tq-spinner" aria-hidden="true" /> : null}
          {isNew ? "Add" : "Save"}
        </button>
      </div>
    </form>
  );
}
